// eslint-disable-next-line max-classes-per-file
import React, { type ReactNode, Component } from 'react';
import ExtensibleError from '@atlassian/jira-extensible-error/src/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import {
	type AGGError,
	type ErrorMeta,
	stopCaptureGraphQlErrors,
} from '@atlassian/jira-relay-errors/src/index.tsx';
import { BacklogSlaTracker } from '@atlassian/jira-software-backlog/src/view/backlog-sla-tracker/index.tsx';
import { isUserNetworkError } from '@atlassian/jira-software-sla-tracker/src/services/response-to-sla/index.tsx';

class AGGErrors extends ExtensibleError {
	name = 'AGGError';

	graphQLErrors: AGGError[];

	traceId?: string;

	constructor(errors: AGGError[], traceId?: string) {
		// this is used to generate same 'hash' for error flag and sla event attribute
		super(errors[0].message, { cause: errors[0] });

		this.graphQLErrors = errors;
		this.traceId = traceId;
	}
}

type Props = {
	errorCaptureKey: string;
	backlogRootQueryName: string;
	children: ReactNode;
};

type State = {
	error: Error | null;
	capturedErrors: AGGError[] | null;
	capturedErrorsMeta?: ErrorMeta | null;
};

// eslint-disable-next-line jira/react/no-class-components
export class BacklogCriticalDataBoundary extends Component<Props, State> {
	static getDerivedStateFromError(error: Error) {
		return {
			error,
		};
	}

	static getDerivedStateFromProps(props: Props, state: State) {
		if (state.error && !state.capturedErrors) {
			// Look for errors in backend response
			const capturedGQLErrorsInfo = stopCaptureGraphQlErrors(props.errorCaptureKey);
			const validGQLErrorsInfo = capturedGQLErrorsInfo.find(
				(err) => err.meta.operationName === props.backlogRootQueryName,
			);

			if (validGQLErrorsInfo?.errors.length) {
				return {
					...state,
					capturedErrors: validGQLErrorsInfo.errors,
					capturedErrorsMeta: validGQLErrorsInfo.meta,
				};
			}
		}
		return null;
	}

	constructor(props: Props) {
		super(props);
		this.state = {
			error: null,
			capturedErrors: null,
		};
	}

	render() {
		/* If there are no caught errors, render children as usual.
           This could mean there are optional GraphQL errors which won't be captured. If needed use @required to capture them. */
		if (!this.state.error) {
			return this.props.children;
		}

		if (this.state.capturedErrors?.length) {
			const aggError = new AGGErrors(
				this.state.capturedErrors,
				this.state.capturedErrorsMeta?.requestId,
			);
			return <BacklogSlaTracker error={aggError} />;
		}

		// Capture the following errors related to backlog critical data the endpoint:
		// 1. Network errors
		// 2. RelayNetwork error - No data returned for operation
		if (
			this.state.error instanceof FetchError ||
			isUserNetworkError(this.state.error) ||
			isClientFetchError(this.state.error) ||
			this.state.error.name === 'RelayNetwork'
		) {
			return <BacklogSlaTracker error={this.state.error} />;
		}

		/* If the error is not something related to the backlog critical data, which we specifically handle above,
           we need to throw so upper error boundary (ExperienceTrackerProvider) can catch and handle it.
           These also include clientside Relay @required errors which doesn't have a corresponding GraphQL error in the backend response */
		throw this.state.error;
	}
}
